import type { ApiError as ApiErrorBody, AuthResponse } from "@bioflow/contracts";
import { getInitData } from "../lib/telegram";

/**
 * По умолчанию адрес относительный: в dev фронт и api живут на одном origin
 * через прокси Vite (/api → api без префикса), и туннель один на весь стенд.
 *
 * В проде витрина и api — разные сервисы на разных доменах, поэтому адрес
 * приходит переменной сборки VITE_API_URL и запекается в бандл. Cookie не
 * используются (токен — Bearer в памяти), так что хватает обычного CORS.
 */
const BASE_URL = (import.meta.env.VITE_API_URL || "/api").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Array<{ path: string; message: string }> = [],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let token: string | null = null;
let authPromise: Promise<string> | null = null;

/**
 * Логин по initData. Возвращает null, если Telegram-контекста нет: тогда
 * работаем анонимно, и публичные эндпоинты (товар, отзывы) всё равно отвечают.
 *
 * Промис кэшируется: на старте несколько компонентов дёргают api
 * одновременно, и без этого получилось бы столько же параллельных логинов.
 */
async function authenticate(): Promise<string | null> {
  if (token) return token;

  const initData = getInitData();
  if (!initData) return null;

  authPromise ??= (async () => {
    const response = await fetch(`${BASE_URL}/auth/telegram`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ initData }),
    });
    if (!response.ok) throw new ApiError(response.status, "auth_failed", "Не удалось войти через Telegram");

    const data = (await response.json()) as AuthResponse;
    token = data.token;
    return data.token;
  })();

  try {
    return await authPromise;
  } finally {
    authPromise = null;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  /** Не пытаться авторизоваться вообще — для заведомо публичных запросов. */
  anonymous?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...options.headers };
  if (options.body !== undefined) headers["content-type"] = "application/json";

  if (!options.anonymous) {
    const bearer = await authenticate();
    // Токена может не быть — вне Telegram. Публичные эндпоинты ответят,
    // приватные вернут 401, и это корректное поведение.
    if (bearer) headers.authorization = `Bearer ${bearer}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  // Токен живёт сутки, приложение может провисеть дольше: один раз
  // перелогиниваемся и повторяем запрос.
  if (response.status === 401 && !options.anonymous && token) {
    token = null;
    return api<T>(path, options);
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      payload?.error.code ?? "request_failed",
      payload?.error.message ?? `Ошибка запроса (${response.status})`,
      payload?.error.details ?? [],
    );
  }

  return (await response.json()) as T;
}
